import type { ReactNode } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import { company } from "@/data/site";
import { getServiceImage } from "@/data/serviceImages";
import { getPostBySlug } from "@/lib/blog.functions";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Resolve an `image:audience/slug` marker to a bundled asset URL, or null.
function resolveImageMarker(url: string): string | null {
  const m = url.match(/^image:(residential|commercial)\/(.+)$/);
  if (!m) return null;
  return getServiceImage(m[1] as "residential" | "commercial", m[2]) ?? null;
}

// Render a single block of inline text with **bold** and [text](url) markdown.
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Combined regex: links or bold
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] && m[2]) {
      const href = m[2];
      const isExternal = /^https?:\/\//.test(href) || href.startsWith("tel:") || href.startsWith("mailto:");
      nodes.push(
        <a
          key={`l-${key++}`}
          href={href}
          {...(isExternal ? {} : {})}
          className="text-accent font-semibold underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
        >
          {m[1]}
        </a>,
      );
    } else if (m[3]) {
      nodes.push(<strong key={`b-${key++}`} className="font-semibold text-primary">{m[3]}</strong>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function MidArticleCTA() {
  return (
    <aside className="my-10 rounded-2xl bg-gradient-hero p-6 text-primary-foreground shadow-elegant sm:p-8">
      <h3 className="text-xl font-extrabold sm:text-2xl">Want a pro to handle it?</h3>
      <p className="mt-2 text-primary-foreground/85">
        Free quotes, same-day responses, and fully insured crews across Broward County.
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
          <Link to="/contact">Get a Free Quote</Link>
        </Button>
        <Button asChild size="lg" className="bg-white/10 text-primary-foreground hover:bg-white/20">
          <Link to="/contact">
            <Phone className="mr-2 h-4 w-4" />
            {company.phone}
          </Link>
        </Button>
      </div>
    </aside>
  );
}

function renderMarkdown(md: string) {
  // Lightweight markdown rendering: H2, blockquote, paragraphs.
  // After parsing into blocks we inject a mid-article CTA after the
  // section roughly in the middle of the article so every post has at
  // least one prominent inline conversion point.
  const blocks = md.split(/\n\n+/);

  // Find H2 indices to pick a midpoint insertion target.
  const h2Indices = blocks
    .map((b, i) => (b.startsWith("## ") ? i : -1))
    .filter((i) => i >= 0);
  const midH2Index = h2Indices.length >= 3 ? h2Indices[Math.floor(h2Indices.length / 2)] : -1;

  const out: ReactNode[] = [];
  blocks.forEach((block, i) => {
    const trimmed = block.trim();
    // Image-only paragraph: ![alt](url)
    const imgMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      const alt = imgMatch[1];
      const rawUrl = imgMatch[2];
      const src = resolveImageMarker(rawUrl) ?? rawUrl;
      out.push(
        <figure key={`img-${i}`} className="my-8 overflow-hidden rounded-2xl border border-border shadow-elegant">
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className="h-auto w-full object-cover"
          />
          {alt && (
            <figcaption className="bg-card px-4 py-2 text-xs text-muted-foreground">{alt}</figcaption>
          )}
        </figure>,
      );
    } else if (block.startsWith("## ")) {
      out.push(
        <h2 key={`h-${i}`} className="mt-10 text-2xl font-extrabold text-primary">
          {renderInline(block.replace(/^##\s+/, ""))}
        </h2>,
      );
    } else if (block.startsWith("> ")) {
      const text = block.replace(/^>\s*\*\*TLDR:\*\*\s*/i, "").replace(/^>\s*/, "");
      out.push(
        <div key={`t-${i}`} className="my-6 rounded-xl border-l-4 border-accent bg-accent/5 p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-accent">TLDR</div>
          <p className="mt-2 text-foreground/90">{renderInline(text)}</p>
        </div>,
      );
    } else {
      out.push(
        <p key={`p-${i}`} className="mt-4 leading-relaxed text-foreground/90">
          {renderInline(block)}
        </p>,
      );
    }
    // Insert mid-article CTA right after the midpoint H2's following paragraph
    if (midH2Index >= 0 && i === midH2Index + 1) {
      out.push(<MidArticleCTA key={`cta-${i}`} />);
    }
  });
  return out;
}

export const Route = createFileRoute("/expert-tips/$citySlug/$slug")({
  head: ({ loaderData }) => {
    const post = (loaderData as any)?.post;
    if (!post) return {};
    const url = `https://mckay.steadyautogrowthdemo.com/expert-tips/${post.city_slug}/${post.slug}`;
    const cityName = String(post.city_slug).replace(/-/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
    return {
      meta: [
        { title: post.meta_title || post.title },
        { name: "description", content: post.meta_description },
        { property: "og:title", content: post.meta_title || post.title },
        { property: "og:description", content: post.meta_description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "article:published_time", content: post.published_at },
        { property: "article:modified_time", content: post.updated_at || post.published_at },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ...(post.schema_json ? [{ type: "application/ld+json", children: JSON.stringify(post.schema_json) }] : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.meta_description,
            datePublished: post.published_at,
            dateModified: post.updated_at || post.published_at,
            author: { "@type": "Organization", name: "Coastal Surface Experts" },
            publisher: {
              "@type": "Organization",
              name: "Coastal Surface Experts",
              logo: { "@type": "ImageObject", url: "https://mckay.steadyautogrowthdemo.com/favicon.png" },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://mckay.steadyautogrowthdemo.com/" },
              { "@type": "ListItem", position: 2, name: "Expert Tips", item: "https://mckay.steadyautogrowthdemo.com/expert-tips" },
              { "@type": "ListItem", position: 3, name: cityName, item: `https://mckay.steadyautogrowthdemo.com/expert-tips/${post.city_slug}` },
              { "@type": "ListItem", position: 4, name: post.title, item: url },
            ],
          }),
        },
        ...((post.faq_json && Array.isArray(post.faq_json) && post.faq_json.length > 0)
          ? [{
              type: "application/ld+json" as const,
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: post.faq_json.map((f: any) => ({
                  "@type": "Question",
                  name: f.question,
                  acceptedAnswer: { "@type": "Answer", text: f.answer },
                })),
              }),
            }]
          : []),
      ],
    };
  },
  loader: async ({ params }) => {
    const { post } = await getPostBySlug({ data: { citySlug: params.citySlug, slug: params.slug } });
    if (!post) throw notFound();
    return { post };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-extrabold text-primary">Article not found</h1>
        <Link to="/expert-tips" className="mt-4 inline-block text-accent font-semibold hover:underline">Back to all tips</Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-8 text-center">
      <p className="text-destructive">Error loading article: {error.message}</p>
    </div>
  ),
  component: PostPage,
});

function PostPage() {
  const { post } = Route.useLoaderData() as any;
  const faqs: { question: string; answer: string }[] = post.faq_json || [];
  const links: { label: string; href: string }[] = post.internal_links || [];

  // Fallback hero image for older posts that don't embed an image marker.
  const audience: "residential" | "commercial" = post.audience === "commercial" ? "commercial" : "residential";
  const fallbackSlug = audience === "residential" ? "house-washing" : "building-washing";
  const fallbackHero = !/!\[[^\]]*\]\(image:/.test(post.body_markdown || "")
    ? getServiceImage(audience, fallbackSlug)
    : null;

  // Split related blog posts (links to /expert-tips/...) from generic links.
  const relatedBlogs = links.filter((l) => l.href.startsWith("/expert-tips/") && l.href.split("/").length >= 4);
  const otherLinks = links.filter((l) => !relatedBlogs.includes(l));

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <article className="mx-auto max-w-3xl px-4 pb-12 pt-24 sm:px-6 md:pt-32 lg:px-8">
          <nav className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Link to="/expert-tips" className="hover:text-accent">Expert Tips</Link>
            <span> › </span>
            <Link to="/expert-tips/$citySlug" params={{ citySlug: post.city_slug }} className="hover:text-accent capitalize">
              {post.city_slug.replace(/-/g, " ")}
            </Link>
          </nav>

          <h1 className="mt-4 text-4xl font-extrabold text-primary sm:text-5xl text-balance">{post.title}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {new Date(post.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
          </p>

          {fallbackHero && (
            <figure className="mt-8 overflow-hidden rounded-2xl border border-border shadow-elegant">
              <img src={fallbackHero} alt={post.title} className="h-auto w-full object-cover" />
            </figure>
          )}

          <div className="prose prose-lg mt-8 max-w-none">
            {renderMarkdown(post.body_markdown)}
          </div>

          {faqs.length > 0 && (
            <section className="mt-12">
              <h2 className="text-2xl font-extrabold text-primary">Frequently asked questions</h2>
              <Accordion type="single" collapsible className="mt-4">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left font-semibold">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-foreground/85">{renderInline(f.answer)}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          {relatedBlogs.length > 0 && (
            <section className="mt-12">
              <h2 className="text-2xl font-extrabold text-primary">Related articles</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {relatedBlogs.slice(0, 4).map((l, i) => (
                  <a
                    key={i}
                    href={l.href}
                    className="group rounded-2xl border border-border bg-card p-5 transition hover:border-accent hover:shadow-elegant"
                  >
                    <div className="text-xs font-bold uppercase tracking-wider text-accent">Expert Tips</div>
                    <div className="mt-2 font-semibold text-primary group-hover:text-accent">{l.label}</div>
                    <div className="mt-3 text-sm font-semibold text-accent">Read article →</div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {otherLinks.length > 0 && (
            <section className="mt-12 rounded-2xl border border-border bg-card p-6">
              <div className="text-sm font-bold uppercase tracking-wider text-secondary">Helpful links</div>
              <ul className="mt-3 space-y-2">
                {otherLinks.map((l, i) => (
                  <li key={i}>
                    <a href={l.href} className="text-accent font-semibold hover:underline">{l.label} →</a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </article>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
